// Phase 9.1 — Status pill + status chip helpers (shared across pages).
//
// Colors follow tailwind defaults so no theme file changes needed.
// Deliberate palette (not the AI-slop violet gradient):
//   out_of_stock -> red      (destructive)
//   danger       -> amber
//   reorder      -> yellow
//   ok           -> emerald
//   overstocked  -> blue
//   unknown      -> muted / slate

import { Badge } from "@/components/ui/badge";

export const STATUS_META = {
  out_of_stock: {
    label: "Out of stock",
    chip: "bg-red-100 text-red-800 border-red-300",
    pill: "bg-red-100 text-red-800 border-red-300",
    cell: "bg-red-50",
  },
  danger: {
    label: "Danger",
    chip: "bg-amber-100 text-amber-900 border-amber-300",
    pill: "bg-amber-100 text-amber-900 border-amber-300",
    cell: "bg-amber-50",
  },
  reorder: {
    label: "Reorder",
    chip: "bg-yellow-100 text-yellow-900 border-yellow-300",
    pill: "bg-yellow-100 text-yellow-900 border-yellow-300",
    cell: "bg-yellow-50",
  },
  ok: {
    label: "OK",
    chip: "bg-emerald-100 text-emerald-900 border-emerald-300",
    pill: "bg-emerald-100 text-emerald-900 border-emerald-300",
    cell: "",
  },
  overstocked: {
    label: "Overstocked",
    chip: "bg-blue-100 text-blue-900 border-blue-300",
    pill: "bg-blue-100 text-blue-900 border-blue-300",
    cell: "bg-blue-50",
  },
  unknown: {
    label: "Unknown",
    chip: "bg-slate-100 text-slate-700 border-slate-300",
    pill: "bg-slate-100 text-slate-700 border-slate-300",
    cell: "",
  },
  not_applicable: {
    label: "N/A",
    chip: "bg-slate-100 text-slate-500 border-slate-200",
    pill: "bg-slate-50 text-slate-500 border-slate-200",
    cell: "bg-slate-50/60",
  },
  inactive_here: {
    label: "Inactive here",
    chip: "bg-slate-100 text-slate-500 border-slate-200",
    pill: "bg-slate-50 text-slate-500 border-slate-200",
    cell: "bg-slate-50/60",
  },
  slow_moving: {
    label: "Slow moving",
    chip: "bg-indigo-100 text-indigo-800 border-indigo-200",
    pill: "bg-indigo-100 text-indigo-800 border-indigo-200",
    cell: "bg-indigo-50/40",
  },
  // Phase 9.7.1 — stock > 0 but no `fb_item_ordering_config` row.
  // Rendered muted (slate) — worth a look but not urgent. Never
  // co-occurs with `reorder`/`ok` on the same row.
  unconfigured: {
    label: "Unconfigured",
    chip: "bg-slate-100 text-slate-700 border-slate-300",
    pill: "bg-slate-100 text-slate-700 border-slate-300",
    cell: "bg-slate-50/60",
  },
};

export const STATUS_ORDER = [
  "out_of_stock", "danger", "reorder", "unconfigured", "ok", "overstocked", "unknown", "slow_moving", "inactive_here", "not_applicable",
];

export function StatusPill({ status, testid }) {
  const meta = STATUS_META[status] || STATUS_META.unknown;
  return (
    <Badge
      variant="outline"
      className={`${meta.pill} text-[10px] uppercase tracking-wider font-medium`}
      data-testid={testid || `status-pill-${status}`}
    >
      {meta.label}
    </Badge>
  );
}

export function StatusChip({ status, count, active, onClick, testid }) {
  const meta = STATUS_META[status] || STATUS_META.unknown;
  const activeRing = active ? "ring-2 ring-offset-1 ring-foreground/40" : "";
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        `${meta.chip} ${activeRing} border rounded-md px-3 py-2 text-left ` +
        "transition-all hover:shadow-sm min-w-[110px]"
      }
      data-testid={testid || `status-chip-${status}`}
    >
      <div className="text-2xl font-semibold leading-none" data-testid={`${testid || `status-chip-${status}`}-count`}>
        {count.toLocaleString()}
      </div>
      <div className="text-[10px] uppercase tracking-wider mt-1 opacity-80">
        {meta.label}
      </div>
    </button>
  );
}

export function fmtNum(v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "number") {
    return v % 1 === 0 ? v.toLocaleString() : v.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  return String(v);
}
